import React from "react";
import { Link } from "react-router-dom";
import {
  Monitor,
  Wrench,
  Search,
  Facebook,
  MousePointerClick,
  Magnet,
  ArrowUpRight,
  ImageIcon,
  UserRound,
  Target,
  PackageCheck,
  CalendarClock,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import { CASE_IMAGES, CASE_LINKS } from "@/data/site";

const STATS = [
  { value: "100,000+", label: "Accounts Reached" },
  { value: "5,500+", label: "Link Clicks" },
  { value: "#1", label: "Google Rankings" },
  { value: "12-Month", label: "Growth Plans" },
];

const SERVICES = [
  { icon: Monitor, title: "Web Design", text: "Fast, professional websites built to convert." },
  { icon: Wrench, title: "Website Maintenance", text: "Updates, hosting and security handled for you." },
  { icon: Search, title: "SEO", text: "Rank at the top of Google in Surrey." },
  { icon: Facebook, title: "Meta Ads", text: "Targeted Facebook & Instagram campaigns." },
  { icon: MousePointerClick, title: "Google Ads", text: "High-intent clicks that become customers." },
  { icon: Magnet, title: "Lead Generation", text: "A steady stream of enquiries, on autopilot." },
];

const SOHO_STATS = [
  { value: "#1", label: "on Google and Google Maps" },
  { value: "49,000", label: "Meta accounts reached" },
  { value: "4,600", label: "link clicks" },
];

const WHY = [
  {
    icon: UserRound,
    title: "Owner-Managed",
    text: "You deal with the owner directly — no account managers.",
  },
  {
    icon: Target,
    title: "Results First",
    text: "Every decision is based on what wins you more customers.",
  },
  {
    icon: PackageCheck,
    title: "All-Inclusive",
    text: "We handle everything from the build to ongoing marketing.",
  },
  {
    icon: CalendarClock,
    title: "12-Month Growth Plan",
    text: "Real results take time and we are with you every step.",
  },
];

export default function HomeSections() {
  return (
    <div className="bg-[#0a1f3d] text-white">
      {/* STATS BAR */}
      <Reveal>
        <section className="border-y border-white/10 bg-[#081831]">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-5 py-12 sm:px-8 md:grid-cols-4">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-4xl font-extrabold text-[#e9a31e] sm:text-4xl">
                  {s.value}
                </p>
                <p className="mt-1 text-base text-white/70 sm:text-base">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </section>
      </Reveal>

      {/* WHAT WE DO */}
      <Reveal>
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <h2 className="text-4xl font-extrabold tracking-tight sm:text-5xl">
          What We Do
        </h2>
        <p className="mt-4 max-w-2xl text-lg text-white/70">
          Everything your business needs to get found online and turn visitors
          into customers.
        </p>

        <div className="mt-12 grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-3">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <Link
                key={s.title}
                to="/services"
                className="group rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition-all duration-200 hover:-translate-y-1 hover:border-[#e9a31e]/40 sm:p-7"
              >
                <div className="flex items-center justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#e9a31e]/15 text-[#e9a31e]">
                    <Icon className="h-6 w-6" />
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-white/25 transition-colors group-hover:text-[#e9a31e]" />
                </div>
                <h3 className="mt-5 text-xl font-bold">{s.title}</h3>
                <p className="mt-2 text-white/70">{s.text}</p>
              </Link>
            );
          })}
        </div>
      </section>
      </Reveal>

      {/* REAL RESULTS */}
      <Reveal>
      <section className="bg-[#0a1f3d]">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <h2 className="text-4xl font-extrabold tracking-tight sm:text-5xl">
            Real <span className="text-[#e9a31e]">Results</span>
          </h2>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {/* SOHO.69 card */}
            <article className="flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
              <img
                src="https://customer-assets-eiarnc6j.emergentagent.net/wingman/1b579b6a-2d4e-44eb-97e9-b3d7a8a3bfc9/attachments/1db58ba81d3344e49203492ba15e4036_f0b6fcc0-6bb8-41ce-9313-974584f2f9da.jpeg"
                alt="SOHO.69 Tenerife"
                className="h-56 w-full object-cover object-center"
              />
              <div className="flex flex-1 flex-col p-7">
                <h3 className="text-2xl font-extrabold">SOHO.69 Tenerife</h3>

                <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-[#e9a31e]">
                  In the first month:
                </p>
                <div className="mt-3 grid grid-cols-3 gap-3">
                  {[
                    { value: "49,000", label: "Reached" },
                    { value: "4,600", label: "Link Clicks" },
                    { value: "#1", label: "on Google" },
                  ].map((s) => (
                    <div
                      key={s.label}
                      className="rounded-xl border border-white/10 bg-[#081831] p-3 text-center"
                    >
                      <p className="text-xl font-extrabold text-[#e9a31e] sm:text-2xl">
                        {s.value}
                      </p>
                      <p className="mt-1 text-[11px] text-white/60">{s.label}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/case-studies"
                    className="flex-1 rounded-lg bg-[#e9a31e] px-5 py-3 text-center text-sm font-bold text-[#0a1f3d] transition-colors hover:bg-[#f2b743]"
                  >
                    View Case Study
                  </Link>
                  <a
                    href={CASE_LINKS.soho}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 rounded-lg border border-white/40 bg-white/5 px-5 py-3 text-center text-sm font-bold text-white transition-colors hover:border-white hover:bg-white/10"
                  >
                    Visit Website
                  </a>
                </div>
              </div>
            </article>

            {/* Mobile Kitchen Company card */}
            <article className="flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
              <img
                src={CASE_IMAGES.mkc}
                alt="Mobile Kitchen Company"
                className="h-56 w-full object-cover"
              />
              <div className="flex flex-1 flex-col p-7">
                <h3 className="text-2xl font-extrabold">Mobile Kitchen Company</h3>
                <p className="mt-4 flex-1 text-white/75">
                  Professional website built from scratch, now ranking in Surrey
                  search results.
                </p>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/case-studies"
                    className="flex-1 rounded-lg bg-[#e9a31e] px-5 py-3 text-center text-sm font-bold text-[#0a1f3d] transition-colors hover:bg-[#f2b743]"
                  >
                    View Case Study
                  </Link>
                  <a
                    href={CASE_LINKS.mkc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 rounded-lg border border-white/40 bg-white/5 px-5 py-3 text-center text-sm font-bold text-white transition-colors hover:border-white hover:bg-white/10"
                  >
                    Visit Website
                  </a>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>
      </Reveal>

      {/* WHY ONLINE OFFICE SOLUTIONS */}
      <Reveal>
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <h2 className="text-4xl font-extrabold tracking-tight sm:text-5xl">
          Why <span className="text-[#e9a31e]">Online Office Solutions</span>
        </h2>

        <div className="mt-6 max-w-4xl space-y-5 text-lg leading-relaxed text-white">
          <p>
            Online Office Solutions is a Surrey-based digital agency run by one
            person who handles your account personally from start to finish. No
            account managers, no junior handoffs, no being passed around — just
            straightforward, results-focused work from someone who genuinely
            cares whether your business grows.
          </p>
          <p>
            We specialise in web design, digital marketing, SEO, and lead
            generation for local businesses across Surrey and the South East.
            Whether you are starting from scratch or looking to scale, we build
            you a complete online presence and then grow it — handling
            everything so you can focus on what you do best.
          </p>
        </div>
      </section>
      </Reveal>

      {/* CTA BANNER */}
      <Reveal>
      <section className="px-5 pb-20 sm:px-8">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-6 rounded-2xl bg-gradient-to-br from-[#e9a31e] to-[#f2b743] p-8 sm:p-10 md:flex-row md:items-center md:justify-between">
          <h2 className="text-3xl font-extrabold tracking-tight text-[#0a1f3d] sm:text-4xl">
            Ready to grow your business online?
          </h2>
          <Link
            to="/audit"
            className="w-full shrink-0 rounded-lg bg-[#0a1f3d] px-7 py-4 text-center text-base font-bold text-white transition-colors hover:bg-[#081831] md:w-auto"
          >
            Book Your Free Audit
          </Link>
        </div>
      </section>
      </Reveal>
    </div>
  );
}
